import AvatarGroup from '@atlaskit/avatar-group';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { ConfluenceIcon, JiraProductDiscoveryIcon, LoomIcon } from '@atlaskit/logo';
import { Anchor, Box, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { css } from '@compiled/react';
import { type Product } from '@atlassian/expansion-services/services';
import { StatusFlag } from '@post-office/components--in-app/status-flag';
import { type FunctionComponent, type ReactNode, type MouseEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { Collaborator, TargetProduct } from '../types';

const logoByVariant: Record<TargetProduct, ReactNode> = {
	loom: <LoomIcon appearance="brand" shouldUseNewLogoDesign size="xxsmall" />,
	'jira-product-discovery': (
		<JiraProductDiscoveryIcon appearance="brand" shouldUseNewLogoDesign size="xxsmall" />
	),
	confluence: <ConfluenceIcon appearance="brand" shouldUseNewLogoDesign size="xxsmall" />,
};

const facePileBorderColorByVariant: Record<TargetProduct, string> = {
	loom: token('color.border.brand'),
	confluence: token('color.border.brand'),
	'jira-product-discovery': token('color.border.discovery'),
};

const inlineLogoStyles = css({
	display: 'inline-flex',
	lineHeight: 0,
	transformOrigin: 'center',
	transitionDuration: token('motion.duration.short'),
	transitionProperty: 'transform',
	transitionTimingFunction: token('motion.easing.inout.bold'),
	verticalAlign: 'middle',
	'&:hover': {
		transform: 'rotate(-6deg) scale(1.1)',
	},
	'@media (prefers-reduced-motion: reduce)': {
		transitionProperty: 'none',
		'&:hover': {
			transform: 'none',
		},
	},
});

const styles = cssMap({
	facePile: {
		pointerEvents: 'none',
	},
	link: {
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'underline',
		},
	},
	inlineBox: {
		display: 'inline-block',
	},
});

const renderInlineLogo = (logo: ReactNode) => <span css={inlineLogoStyles}>{logo}</span>;

type BodyProps = {
	targetProduct: TargetProduct;
	collaborators: Collaborator[];
};

export const productNameByVariant: Record<TargetProduct, ReactNode> = {
	loom: (
		<FormattedMessage
			id="puma.common.side-nav.loom.product-name"
			description="Product name for Loom, shown inline in the cross-join recommendation"
			defaultMessage="Loom"
		/>
	),
	'jira-product-discovery': (
		<FormattedMessage
			id="puma.common.side-nav.jpd.product-name"
			description="Product name for Jira Product Discovery, shown inline in the cross-join recommendation"
			defaultMessage="Jira Product Discovery"
		/>
	),
	confluence: (
		<FormattedMessage
			id="puma.common.side-nav.confluence.product-name"
			description="Product name for Confluence, shown inline in the cross-join recommendation"
			defaultMessage="Confluence"
		/>
	),
};

const FacePile: FunctionComponent<BodyProps> = ({ targetProduct, collaborators }) => {
	const { formatMessage } = useIntl();

	if (collaborators.length === 0) {
		return null;
	}

	const avatarBorderColor = facePileBorderColorByVariant[targetProduct];

	return (
		<Box xcss={styles.facePile}>
			<AvatarGroup
				appearance="stack"
				size="small"
				maxCount={2}
				data={collaborators.map(({ name, picture }) => ({
					name,
					src: picture,
					borderColor: avatarBorderColor,
				}))}
				overrides={{
					MoreIndicator: {
						render: (Component, props) => (
							<Component {...props} borderColor={token('elevation.surface.overlay')} />
						),
					},
				}}
				isTooltipDisabled
				onMoreClick={() => {}}
				label={formatMessage({
					id: 'puma.common.side-nav.face-pile.label',
					description: 'Accessible label for the face pile of collaborators using the product',
					defaultMessage: 'Collaborators using this product',
				})}
			/>
		</Box>
	);
};

const Description: FunctionComponent<BodyProps> = ({ targetProduct, collaborators }) => {
	const productName = productNameByVariant[targetProduct];
	const logo = logoByVariant[targetProduct];
	const [firstCollaborator, ...rest] = collaborators;

	if (!firstCollaborator) {
		return (
			<Heading size="xsmall">
				<FormattedMessage
					id="puma.common.side-nav.description.team"
					description="Cross-join recommendation copy shown when the user has no named collaborators on the recommended product"
					defaultMessage="Your team is using <InlineBlock><Logo></Logo> {productName}</InlineBlock>"
					values={{
						productName,
						Logo: () => renderInlineLogo(logo),
						InlineBlock: (chunks) => <Box xcss={styles.inlineBox}>{chunks}</Box>,
					}}
				/>
			</Heading>
		);
	}

	if (rest.length === 0) {
		return (
			<Heading size="xsmall">
				<FormattedMessage
					id="puma.common.side-nav.description.single"
					description="Cross-join recommendation copy shown when exactly one of the user's collaborators is using the recommended product"
					defaultMessage="{firstCollaborator} is using <InlineBlock><Logo></Logo> {productName}</InlineBlock>"
					values={{
						firstCollaborator: firstCollaborator.name,
						productName,
						Logo: () => renderInlineLogo(logo),
						InlineBlock: (chunks) => <Box xcss={styles.inlineBox}>{chunks}</Box>,
					}}
				/>
			</Heading>
		);
	}

	return (
		<Heading size="xsmall">
			<FormattedMessage
				id="puma.common.side-nav.description.multiple"
				description="Cross-join recommendation copy shown when two or more of the user's collaborators are using the recommended product"
				defaultMessage="{firstCollaborator} and {collaboratorCount, plural, one {# other} other {# others}} are using <InlineBlock><Logo></Logo> {productName}</InlineBlock>"
				values={{
					firstCollaborator: firstCollaborator.name,
					collaboratorCount: rest.length,
					productName,
					Logo: () => renderInlineLogo(logo),
					InlineBlock: (chunks) => <Box xcss={styles.inlineBox}>{chunks}</Box>,
				}}
			/>
		</Heading>
	);
};

type Props = BodyProps & {
	tenantId: string;
	userId: string;
	collaborators: Collaborator[];
	sourceProduct: Product;
	crossJoinUrl: string;
	messageTemplateId?: string;
	onRequestAccess: (event: MouseEvent<HTMLAnchorElement>) => Promise<void>;
	isRequestSentFlagVisible: boolean;
	setIsRequestSentFlagVisible: (state: boolean) => void;
};

export const Body: FunctionComponent<Props> = ({
	targetProduct,
	collaborators,
	crossJoinUrl,
	onRequestAccess,
	isRequestSentFlagVisible,
	setIsRequestSentFlagVisible,
}) => {
	const productName = productNameByVariant[targetProduct];

	return (
		<Stack space="space.100">
			<FacePile targetProduct={targetProduct} collaborators={collaborators} />
			<Description targetProduct={targetProduct} collaborators={collaborators} />
			<Anchor href={crossJoinUrl} xcss={styles.link} onClick={onRequestAccess}>
				<FormattedMessage
					id="puma.common.side-nav.request-access-link"
					description="Call-to-action link inviting the user to request access to the recommended product so they can join their team"
					defaultMessage="Join your team"
				/>
			</Anchor>
			{isRequestSentFlagVisible && (
				<StatusFlag
					status="success"
					title={
						<FormattedMessage
							id="puma.common.side-nav.request-sent-flag.title"
							description="Title of the toast shown after the user successfully requests to join their team on the recommended product"
							defaultMessage="Nearly there!"
						/>
					}
					description={
						<FormattedMessage
							id="puma.common.side-nav.request-sent-flag.description"
							description="Body of the toast shown after the user successfully requests to join their team on the recommended product"
							defaultMessage="A quick access check is all that's left. We'll email you the moment you're good to try {productName}"
							values={{ productName }}
						/>
					}
					actions={[]}
					onDismiss={() => setIsRequestSentFlagVisible(false)}
				/>
			)}
		</Stack>
	);
};
